import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { externalLinkProps } from '../utils/url';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import {
  GraduationCap, RefreshCw, Youtube, FileText, Loader2, Clock,
  BookOpen, ExternalLink
} from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const DIFF_STYLES = {
  Beginner:     'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  Intermediate: 'bg-amber-500/15   text-amber-300   border-amber-500/30',
  Advanced:     'bg-rose-500/15    text-rose-300    border-rose-500/30',
};

export default function DailyLearning() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/learning/latest`, { withCredentials: true });
      setDoc(data);
    } catch {
      toast.error('Failed to load daily learning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && !doc) fetchLatest(); }, [open, doc, fetchLatest]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/learning/refresh`, {}, { withCredentials: true });
      toast.info('Rebuilding — refreshing in 30-60s');
      const startedAt = doc?.generated_at;
      const deadline = Date.now() + 3 * 60 * 1000;
      const poll = async () => {
        if (Date.now() > deadline) { setRefreshing(false); return; }
        try {
          const { data } = await axios.get(`${API}/learning/latest`, { withCredentials: true });
          if (data?.generated_at && data.generated_at !== startedAt) {
            setDoc(data); toast.success('Learning refreshed'); setRefreshing(false); return;
          }
        } catch { /* ignore */ }
        setTimeout(poll, 15000);
      };
      setTimeout(poll, 20000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
      setRefreshing(false);
    }
  };

  const items = doc?.items || [];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="daily-learning-trigger"
        className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-gradient-to-r from-cyan-500/15 to-blue-500/15 hover:from-cyan-500/25 hover:to-blue-500/25 border border-cyan-400/30 text-cyan-300 hover:text-white transition-all group"
      >
        <GraduationCap size={14} className="group-hover:scale-110 transition-transform" />
        <span className="text-[11px] font-semibold tracking-wide uppercase">Learn</span>
        <span className="hidden sm:flex items-center gap-1 ml-0.5 text-[10px] opacity-70">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
          Today
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-2xl bg-[#08080d] border-l border-white/10 overflow-y-auto p-0">
          <SheetHeader className="sticky top-0 z-10 bg-gradient-to-b from-[#08080d] via-[#08080d] to-[#08080d]/90 backdrop-blur-xl border-b border-white/5 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <GraduationCap size={18} className="text-white" />
                </div>
                <div>
                  <SheetTitle className="text-white font-heading text-lg">Daily Learning</SheetTitle>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <Clock size={10} />
                    {doc ? `${doc.date} · updated ${timeAgo(doc.generated_at)}` : '—'}
                    {items.length > 0 && <span className="ml-2 text-cyan-400">· {items.length} lessons</span>}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <Button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  variant="outline"
                  className="bg-white/5 border-white/10 text-slate-300 hover:text-white h-8 text-[11px]"
                  data-testid="learning-refresh-btn"
                >
                  {refreshing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                  {refreshing ? 'Rebuilding…' : 'Refresh'}
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="px-6 py-5" data-testid="learning-body">
            {loading && !doc && (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading today's learning…
              </div>
            )}

            {doc && items.length === 0 && (
              <div className="text-center py-12 text-slate-500 text-sm">
                {(() => {
                  const e = String(doc.error || '');
                  if (e.includes('429') || e.includes('RESOURCE_EXHAUSTED') || e.toLowerCase().includes('quota')) {
                    return 'AI quota hit — refreshing tomorrow morning';
                  }
                  if (e.includes('503') || e.includes('UNAVAILABLE')) {
                    return 'Source temporarily unavailable — try refresh in a few minutes';
                  }
                  if (e) return 'Not available right now';
                  return 'No learnings available yet. Admin can refresh.';
                })()}
              </div>
            )}

            {doc?.stale && items.length > 0 && (
              <div className="mb-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Showing yesterday's lessons — fresh set will arrive tomorrow morning.
              </div>
            )}

            <div className="space-y-3">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="glass-card p-4 hover:border-cyan-500/30 transition-colors"
                  data-testid={`learning-item-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-500/25 to-blue-500/25 border border-cyan-500/40 flex items-center justify-center text-cyan-200 font-bold text-xs flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {it.focus_area && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase tracking-wider font-semibold">
                            {it.focus_area}
                          </span>
                        )}
                        {it.difficulty && (
                          <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${DIFF_STYLES[it.difficulty] || DIFF_STYLES.Intermediate}`}>
                            {it.difficulty}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-white mb-1.5 leading-snug">{it.title}</h4>
                      <p className="text-[12px] text-slate-300 leading-relaxed mb-3">{it.tip}</p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {it.youtube_url && (
                          <a
                            {...externalLinkProps(it.youtube_url)}
                            className="group flex items-center gap-2 px-2.5 py-1.5 bg-rose-500/10 hover:bg-rose-500/15 border border-rose-500/20 rounded text-[11px] text-rose-300 hover:text-rose-200 flex-1 min-w-0"
                            data-testid={`learning-${i}-youtube`}
                          >
                            <Youtube size={12} className="flex-shrink-0" />
                            <span className="truncate flex-1">{it.youtube_title || 'Watch tutorial'}</span>
                            <ExternalLink size={10} className="flex-shrink-0 opacity-50 group-hover:opacity-100" />
                          </a>
                        )}
                        {it.article_url && (
                          <a
                            {...externalLinkProps(it.article_url)}
                            className="group flex items-center gap-2 px-2.5 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/15 border border-indigo-500/20 rounded text-[11px] text-indigo-300 hover:text-indigo-200 flex-1 min-w-0"
                            data-testid={`learning-${i}-article`}
                          >
                            <FileText size={12} className="flex-shrink-0" />
                            <span className="truncate flex-1">{it.article_title || 'Read article'}</span>
                            <ExternalLink size={10} className="flex-shrink-0 opacity-50 group-hover:opacity-100" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] text-slate-500 justify-center">
                <BookOpen size={10} />
                Auto-curated fresh each morning at 7:10 IST · India & global signals
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
